import React, { useEffect, useState } from 'react';
import { guestStudentApi } from '../services/api';
import { GuestStudent } from '../types';
import { handleApiError, handleSuccess } from '../utils/errorHandler';
import './GuestStudents.css';

const GuestStudents: React.FC = () => {
  const [guestStudents, setGuestStudents] = useState<GuestStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    contactInfo: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadGuestStudents = async () => {
    setLoading(true);
    try {
      const response = await guestStudentApi.getAll({ search: search.trim() || undefined, limit: 200 });
      setGuestStudents(response.data.guestStudents);
    } catch (error) {
      handleApiError(error as any, 'Failed to load guest students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGuestStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await loadGuestStudents();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      contactInfo: '',
      notes: ''
    });
  };

  const handleCreateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim()) {
      handleApiError({ error: 'Guest name is required' } as any);
      return;
    }

    setSubmitting(true);
    try {
      const response = await guestStudentApi.create({
        fullName: formData.fullName.trim(),
        contactInfo: formData.contactInfo.trim() || undefined,
        notes: formData.notes.trim() || undefined
      });
      setGuestStudents(prev => [response.data.guestStudent, ...prev]);
      handleSuccess('Guest student added');
      resetForm();
    } catch (error) {
      handleApiError(error as any, 'Failed to create guest student');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGuest = async (guest: GuestStudent) => {
    if (!guest.id) return;
    const confirmed = window.confirm(`Remove guest "${guest.fullName}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await guestStudentApi.delete(guest.id);
      setGuestStudents(prev => prev.filter(g => g.id !== guest.id));
      handleSuccess('Guest student removed');
    } catch (error) {
      handleApiError(error as any, 'Unable to delete guest student');
    }
  };

  return (
    <div className="guest-students-page">
      <div className="page-header">
        <div>
          <h1>Guest Student Management</h1>
          <p className="page-subtitle">
            Track consultation-only participants. Guest students can be assigned to schedules and removed when no longer needed.
          </p>
        </div>
        <form className="guest-search" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search guest name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="primary-btn">Search</button>
        </form>
      </div>

      <div className="guest-content">
        <div className="guest-form-card">
          <h3>Add Guest Student</h3>
          <form onSubmit={handleCreateGuest}>
            <div className="form-group">
              <label htmlFor="guestFullName">Guest Name *</label>
              <input
                id="guestFullName"
                name="fullName"
                type="text"
                placeholder="e.g., Jane Smith"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="guestContact">Contact Info</label>
              <input
                id="guestContact"
                name="contactInfo"
                type="text"
                placeholder="Phone or email"
                value={formData.contactInfo}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="guestNotes">Notes</label>
              <textarea
                id="guestNotes"
                name="notes"
                placeholder="Additional details (optional)"
                value={formData.notes}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="secondary-btn" onClick={resetForm} disabled={submitting}>
                Clear
              </button>
              <button type="submit" className="primary-btn" disabled={submitting}>
                {submitting ? 'Saving...' : 'Add Guest'}
              </button>
            </div>
          </form>
        </div>

        <div className="guest-table-card">
          <div className="table-header">
            <h3>Guest Students ({guestStudents.length})</h3>
            <button type="button" className="refresh-btn" onClick={loadGuestStudents} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {guestStudents.length === 0 ? (
            <div className="empty-state">
              {loading ? 'Loading guest students...' : 'No guest students found.'}
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="guest-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Notes</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {guestStudents.map(guest => (
                    <tr key={guest.id}>
                      <td>{guest.fullName}</td>
                      <td>{guest.contactInfo || '—'}</td>
                      <td>{guest.notes || '—'}</td>
                      <td>{guest.createdAt ? new Date(guest.createdAt).toLocaleDateString() : '—'}</td>
                      <td className="actions-cell">
                        <button
                          type="button"
                          className="danger-btn"
                          onClick={() => handleDeleteGuest(guest)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestStudents;
